import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  styled,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DevicesIcon from '@mui/icons-material/Devices';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import SecurityIcon from '@mui/icons-material/Security';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { motion } from 'motion/react';

// Style components for the themed UI - Updated with high-end luxury theme
const GradientBackground = styled(Box)(() => ({
  background: '#231F20',
  position: 'relative',
  overflow: 'hidden',
  color: '#fff',
  minHeight: '100vh',
}));

const GradientOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'radial-gradient(circle at 30% 20%, rgba(142, 121, 62, 0.08) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(173, 151, 79, 0.05) 0%, transparent 50%)',
  zIndex: 0,
}));

const BackgroundLines = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: 'linear-gradient(rgba(173, 151, 79, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(173, 151, 79, 0.02) 1px, transparent 1px)',
  backgroundSize: '40px 40px',
  backgroundPosition: 'center center',
  zIndex: 0,
  opacity: 0.3,
}));

// Update DiagonalStripes to luxury gold
const DiagonalStripes = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `
    linear-gradient(
      135deg,
      transparent 0%,
      transparent 35%,
      rgba(142, 121, 62, 0.15) 35%,
      rgba(142, 121, 62, 0.15) 37%,
      transparent 37%,
      transparent 65%,
      rgba(173, 151, 79, 0.1) 65%,
      rgba(173, 151, 79, 0.1) 67%,
      transparent 67%
    ),
    linear-gradient(
      140deg,
      transparent 0%,
      transparent 15%,
      rgba(173, 151, 79, 0.08) 15%,
      rgba(173, 151, 79, 0.08) 17%,
      transparent 17%,
      transparent 45%,
      rgba(142, 121, 62, 0.12) 45%,
      rgba(142, 121, 62, 0.12) 47%,
      transparent 47%,
      transparent 75%,
      rgba(173, 151, 79, 0.15) 75%,
      rgba(173, 151, 79, 0.15) 77%,
      transparent 77%
    )
  `,
  zIndex: 0,
  opacity: 0.8,
}));

// Update GlossyBackground to luxury gold
const GlossyRedBackground = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `
    linear-gradient(135deg, 
      rgba(0, 0, 0, 0) 0%,
      rgba(142, 121, 62, 0.08) 25%,
      rgba(173, 151, 79, 0.12) 50%,
      rgba(142, 121, 62, 0.08) 75%,
      rgba(0, 0, 0, 0) 100%
    ),
    linear-gradient(120deg,
      rgba(0, 0, 0, 0) 10%,
      rgba(173, 151, 79, 0.1) 30%,
      rgba(142, 121, 62, 0.12) 70%,
      rgba(0, 0, 0, 0) 90%
    )
  `,
  filter: 'blur(5px)',
  zIndex: 0,
  opacity: 0.7,
}));

// Update GlossyStreaks to luxury gold
const GlossyStreaks = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `
    linear-gradient(130deg,
      transparent 0%,
      transparent 40%,
      rgba(173, 151, 79, 0.2) 45%,
      rgba(173, 151, 79, 0.08) 50%,
      transparent 55%
    ),
    linear-gradient(140deg,
      transparent 0%,
      transparent 60%,
      rgba(142, 121, 62, 0.15) 65%,
      rgba(142, 121, 62, 0.08) 70%,
      transparent 75%
    )
  `,
  filter: 'blur(3px)',
  zIndex: 0,
  opacity: 0.6,
}));

// Feature card with luxury design
const FeatureCard = styled(Card)(({ theme }) => ({
  background: '#2A2627',
  backdropFilter: 'blur(8px)',
  borderRadius: '4px',
  border: '1px solid #333333',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
  height: '100%',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 40px rgba(142, 121, 62, 0.15)',
    border: '1px solid rgba(173, 151, 79, 0.3)',
  }
}));

// Pricing card with luxury design
const PricingCard = styled(Card)(({ theme }) => ({
  background: '#2A2627',
  backdropFilter: 'blur(8px)',
  borderRadius: '4px',
  border: '1px solid #333333',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
  height: '100%',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 40px rgba(142, 121, 62, 0.15)',
    border: '1px solid rgba(173, 151, 79, 0.3)',
  }
}));

// Section heading with luxury style
const SectionHeading = styled(Typography)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(1),
  letterSpacing: '1px',
  fontWeight: 600,
  textTransform: 'uppercase',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: -8,
    height: 2,
    width: 40,
    background: '#8E793E',
    borderRadius: 0,
  }
}));

// Modern button with luxury gold
const GradientButton = styled(Button)(({ theme }) => ({
  background: '#8E793E',
  color: '#fff',
  fontWeight: 500,
  padding: '12px 30px',
  borderRadius: '0',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  boxShadow: 'none',
  border: 'none',
  position: 'relative',
  '&:hover': {
    background: '#AD974F',
    boxShadow: '0 5px 15px rgba(142, 121, 62, 0.3)',
    transform: 'translateY(-2px)',
    transition: 'all 0.2s ease'
  }
}));

// Secondary button with luxury design
const GlassButton = styled(Button)(({ theme }) => ({
  background: 'transparent',
  color: '#EAEAEA',
  fontWeight: 500,
  padding: '11px 29px',
  borderRadius: '0',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  border: '1px solid #8E793E',
  boxShadow: 'none',
  '&:hover': {
    background: 'rgba(142, 121, 62, 0.1)',
    border: '1px solid #AD974F',
    boxShadow: '0 5px 15px rgba(142, 121, 62, 0.15)',
    transform: 'translateY(-2px)',
    transition: 'all 0.2s ease'
  }
}));

// Update the Particle component to create more streak-like effects
const Particle = styled(Box)<{
  size: number;
  color: string;
  initialX: number;
  initialY: number;
  duration: number;
  angle: number; // Add angle property
}>(({ size, color, initialX, initialY, duration, angle }) => ({
  position: 'absolute',
  width: `${size * 2}px`,
  height: `${size * 15}px`, // Make particles longer to look like streaks
  backgroundColor: color,
  borderRadius: '30%',
  left: `${initialX}%`,
  top: `${initialY}%`,
  opacity: 0.8,
  zIndex: 0,
  transform: `rotate(${angle}deg)`, // Apply rotation based on angle
  filter: 'blur(2px)',
  animation: `float ${duration}s infinite ease-in-out`,
  '@keyframes float': {
    '0%': {
      transform: `translateX(0px) translateY(0px) rotate(${angle}deg)`,
      opacity: 0.2,
    },
    '50%': {
      transform: `translateX(${Math.random() > 0.5 ? '-' : ''}${Math.floor(Math.random() * 30)}px) translateY(${Math.random() > 0.5 ? '-' : ''}${Math.floor(Math.random() * 30)}px) rotate(${angle}deg)`,
      opacity: 0.8,
    },
    '100%': {
      transform: `translateX(0px) translateY(0px) rotate(${angle}deg)`,
      opacity: 0.2,
    },
  },
}));

// Pricing data
const pricingPlans = [
  {
    name: 'Starter',
    price: '$49',
    period: '/month',
    description: 'Perfect for small auto repair shops',
    features: [
      'Up to 3 mechanics',
      'Basic appointment scheduling',
      'Customer database',
      'Digital invoicing',
      'Email support',
    ],
    highlight: false,
    buttonText: 'Start Free Trial'
  },
  {
    name: 'Professional',
    price: '$99',
    period: '/month',
    description: 'Ideal for growing businesses',
    features: [
      'Up to 10 mechanics',
      'Advanced scheduling',
      'Inventory management',
      'Customer portal',
      'SMS notifications',
      'Parts ordering integration',
      'Priority support',
    ],
    highlight: true,
    buttonText: 'Choose Professional'
  },
  {
    name: 'Enterprise',
    price: '$199',
    period: '/month',
    description: 'For large operations with multiple locations',
    features: [
      'Unlimited mechanics',
      'Multi-location support',
      'Business analytics',
      'Fleet management',
      'Advanced reporting',
      'Custom integrations',
      'Dedicated account manager',
    ],
    highlight: false,
    buttonText: 'Contact Sales'
  }
];

// Features data
const features = [
  {
    icon: <DevicesIcon sx={{ fontSize: 36, color: '#8E793E' }} />,
    title: 'Multi-Platform',
    description: 'Access your shop management system from any device with our responsive web app, iOS and Android applications.'
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 36, color: '#8E793E' }} />,
    title: 'Fast Performance',
    description: 'Optimized for speed so you can manage appointments, invoices, and repairs without delays.'
  },
  {
    icon: <BuildIcon sx={{ fontSize: 36, color: '#8E793E' }} />,
    title: 'Digital Inspections',
    description: 'Create detailed digital vehicle inspections with photos and videos to share with customers.'
  },
  {
    icon: <CloudDoneIcon sx={{ fontSize: 36, color: '#8E793E' }} />,
    title: 'Cloud Storage',
    description: 'All your data is securely stored in the cloud with automatic backups and real-time sync.'
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 36, color: '#8E793E' }} />,
    title: 'Data Security',
    description: 'Enterprise-grade security with encryption and compliance with automotive industry standards.'
  },
  {
    icon: <PeopleIcon sx={{ fontSize: 36, color: '#8E793E' }} />,
    title: 'Customer Portal',
    description: 'Give customers access to their service history, upcoming appointments, and estimate approvals.'
  },
];

const MechanixHubPage: React.FC = () => {
  
  // Generate particles with luxury gold theme
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    size: Math.random() * 1.5 + 0.5,
    color: Math.random() > 0.5 
      ? `rgba(173, 151, 79, 0.${Math.floor(Math.random() * 4 + 2)})`
      : `rgba(142, 121, 62, 0.${Math.floor(Math.random() * 4 + 2)})`,
    initialX: Math.random() * 100,
    initialY: Math.random() * 100,
    duration: Math.random() * 15 + 20,
    angle: Math.floor(Math.random() * 20) + 125
  }));

  return (
    <GradientBackground>
      <GradientOverlay />
      <BackgroundLines />
      <DiagonalStripes />
      <GlossyRedBackground />
      <GlossyStreaks />
      
      {/* Animated background particles */}
      {particles.map((particle) => (
        <Particle
          key={particle.id}
          size={particle.size}
          color={particle.color}
          initialX={particle.initialX}
          initialY={particle.initialY}
          duration={particle.duration}
          angle={particle.angle}
        />
      ))}
      
      <Container maxWidth="lg" sx={{ pt: { xs: 10, md: 16 }, pb: 10, position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Box sx={{ mb: { xs: 10, md: 15 } }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography 
                  variant="h5" 
                  component="p"
                  sx={{ 
                    mb: 2, 
                    color: '#AD974F',
                    fontWeight: 500,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                  }}
                >
                  The Ultimate Auto Shop Management Platform
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Typography 
                  variant="h1" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 500, 
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    mb: 1,
                    color: '#FFFFFF',
                    textTransform: 'uppercase',
                    letterSpacing: '2px'
                  }}
                >
                  High-End & Classy
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#EAEAEA',
                    maxWidth: '600px', 
                    mb: 5,
                    fontWeight: 400,
                    letterSpacing: '0.5px',
                  }}
                >
                  Streamline your auto repair business with our comprehensive management software. 
                  Appointments, invoicing, inventory, and customer management all in one powerful platform.
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <GradientButton
                    size="large"
                  >
                    Start Now
                  </GradientButton>
                  <GlassButton
                    size="large"
                  >
                    Learn More
                  </GlassButton>
                </Box>
              </motion.div>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Box 
                  component="img" 
                  src="/images/hero-illustration.png" 
                  alt="Auto Service Platform" 
                  sx={{ 
                    width: '100%',
                    borderRadius: '4px',
                    filter: 'contrast(1.05) brightness(0.95)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  }} 
                />
              </motion.div>
            </Grid>
          </Grid>
        </Box>
        
        {/* Features Section - update section headings to be consistent */}
        <Box sx={{ mb: 15 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading variant="h3" sx={{ mb: 2, textAlign: 'center' }}>
              Powerful Features
            </SectionHeading>
            <Typography 
              variant="h6" 
              align="center"
              sx={{ 
                color: '#EAEAEA',
                maxWidth: '700px', 
                mx: 'auto',
                mb: 6,
                letterSpacing: '0.5px',
              }}
            >
              Everything you need to run your auto repair business efficiently in one platform
            </Typography>
          </motion.div>

          {/* Features grid */}
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  <FeatureCard>
                    <CardContent>
                      <Box sx={{ mb: 2 }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#EAEAEA' }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Why MechanixHub Section */}
        <Box sx={{ mb: 15 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <SectionHeading variant="h3" sx={{ mb: 2 }}>
                  Distinctive Advantages
                </SectionHeading>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#EAEAEA',
                    maxWidth: '700px', 
                    mb: 4,
                    letterSpacing: '0.5px',
                  }}
                >
                  Built specifically for auto repair shops with features that make your workflow smoother
                </Typography>
              </motion.div>

              <List sx={{ mb: 4 }}>
                {[
                  'Save 15+ hours per week on administrative tasks',
                  'Increase customer satisfaction by 35%',
                  'Reduce no-shows with automated reminders',
                  'Track shop performance with real-time analytics',
                  'Securely store and access customer vehicle history',
                  '24/7 technical support and regular updates'
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 * index }}
                  >
                    <ListItem sx={{ py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleIcon sx={{ color: '#8E793E' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item} 
                        primaryTypographyProps={{ 
                          sx: { color: '#EAEAEA', fontWeight: 400 } 
                        }} 
                      />
                    </ListItem>
                  </motion.div>
                ))}
              </List>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Box 
                  sx={{ 
                    position: 'relative',
                    height: '100%',
                    minHeight: '400px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {/* Show multi-device view with dashboard/app screenshots */}
                  <Box 
                    sx={{ 
                      p: 3,
                      background: 'rgba(35, 31, 32, 0.8)', 
                      borderRadius: '4px',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(142, 121, 62, 0.2)',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 3, color: '#EAEAEA', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      Dashboard Analytics
                    </Typography>
                    <Box 
                      sx={{ 
                        mb: 2,
                        height: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <BarChartIcon sx={{ fontSize: 120, color: '#8E793E' }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#EAEAEA' }}>
                      Track your shop performance metrics, revenue, customer satisfaction scores, and job completion rates
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Box>
        
        {/* Pricing Section */}
        <Box sx={{ mb: 15 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading variant="h3" sx={{ mb: 2, textAlign: 'center' }}>
              Pricing Plans
            </SectionHeading>
            <Typography 
              variant="h6" 
              align="center"
              sx={{ 
                color: '#EAEAEA',
                maxWidth: '700px', 
                mx: 'auto',
                mb: 6,
                letterSpacing: '0.5px',
              }}
            >
              Choose the perfect plan for your shop's needs
            </Typography>
          </motion.div>

          <Grid container spacing={4} justifyContent="center">
            {pricingPlans.map((plan, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 * index }}
                >
                  <PricingCard sx={{ 
                    ...(plan.highlight && {
                      border: '1px solid #8E793E',
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)'
                    })
                  }}>
                    {plan.highlight && (
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: 12, 
                          right: 12, 
                          background: '#8E793E',
                          color: '#FFFFFF',
                          px: 2,
                          py: 0.5,
                          borderRadius: '0',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          letterSpacing: '1px',
                          textTransform: 'uppercase'
                        }}
                      >
                        Most Popular
                      </Box>
                    )}
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h5" sx={{ fontWeight: 500, color: '#fff', mb: 1, textTransform: 'uppercase', letterSpacing: '1px' }}>
                        {plan.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                        <Typography variant="h3" sx={{ fontWeight: 600, color: '#AD974F' }}>
                          {plan.price}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#EAEAEA', ml: 1 }}>
                          {plan.period}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#EAEAEA', mb: 3 }}>
                        {plan.description}
                      </Typography>
                      
                      <Box sx={{ mb: 4 }}>
                        {plan.features.map((feature, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <CheckCircleIcon sx={{ color: '#8E793E', mr: 1.5, fontSize: 18 }} />
                            <Typography variant="body2" sx={{ color: '#EAEAEA' }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      
                      {plan.highlight ? (
                        <GradientButton fullWidth size="large">
                          {plan.buttonText}
                        </GradientButton>
                      ) : (
                        <GlassButton fullWidth size="large">
                          {plan.buttonText}
                        </GlassButton>
                      )}
                    </CardContent>
                  </PricingCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ mb: 10 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Box 
              sx={{ 
                py: 6, 
                px: 4,
                borderRadius: 0,
                background: '#2A2627',
                border: '1px solid #8E793E',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                textAlign: 'center'
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 500, color: '#fff', mb: 2, textTransform: 'uppercase', letterSpacing: '1px' }}>
                Luxurious & Distinctive
              </Typography>
              <Typography variant="h6" sx={{ color: '#EAEAEA', mb: 4, maxWidth: 700, mx: 'auto', letterSpacing: '0.5px' }}>
                Join thousands of auto repair shops already saving time and increasing profits with MechanixHub.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <GradientButton
                  size="large"
                >
                  Start Now
                </GradientButton>
                <GlassButton
                  size="large"
                  startIcon={<SupportAgentIcon />}
                >
                  Contact
                </GlassButton>
              </Box>
              <Typography variant="body2" sx={{ color: '#EAEAEA', mt: 3, opacity: 0.7 }}>
                No credit card required. Cancel anytime.
              </Typography>
            </Box>
          </motion.div>
        </Box>
      </Container>
    </GradientBackground>
  );
};

export default MechanixHubPage; 